import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Search, Users, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { cn } from '@/lib/utils';
import { initialsOf } from '@/components/projects/PeoplePicker';

export interface AssigneesPickerProfile {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url?: string | null;
}

interface AssigneesPickerProps {
  /** Currently-assigned profiles (used to render the trigger). */
  assignees: AssigneesPickerProfile[];
  /** Candidate pool — typically the project's member list. */
  members: AssigneesPickerProfile[];
  /** Called with the FULL desired set of assignee profile ids. */
  onChange: (nextIds: string[]) => void;
  disabled?: boolean;
  /** Override the trigger button's tailwind classes. */
  triggerClassName?: string;
  /** Override the dropdown content's tailwind classes (width, etc.). */
  contentClassName?: string;
  /**
   * Number of avatars to render in the trigger before collapsing the
   * rest into a "+N" chip. Defaults to 3.
   */
  maxAvatars?: number;
}

/**
 * Multi-assignee picker shared by the project detail table row and the
 * task detail drawer. Renders a stacked-avatar trigger that expands
 * into a search-and-toggle dropdown of every project member; selection
 * is committed on each click via `onChange` with the full desired set
 * (not deltas) so the parent can run a single `useSetTaskAssignees`
 * mutation that diffs against the cache.
 *
 * Empty `assignees` → "Unassigned" placeholder.
 * Empty `members`  → renders an explanatory empty state (the operator
 *                    needs to share the project before they can
 *                    assign anyone).
 */
export function AssigneesPicker({
  assignees,
  members,
  onChange,
  disabled = false,
  triggerClassName,
  contentClassName,
  maxAvatars = 3,
}: AssigneesPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Reset the search box every time the menu re-opens so a stale
  // query from a previous edit doesn't filter the new view.
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const selectedIds = useMemo(
    () => new Set(assignees.map((a) => a.id)),
    [assignees],
  );

  const filteredMembers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) =>
      `${m.full_name ?? ''} ${m.email}`.toLowerCase().includes(q),
    );
  }, [members, query]);

  const toggle = (profileId: string) => {
    const next = new Set(selectedIds);
    if (next.has(profileId)) next.delete(profileId);
    else next.add(profileId);
    onChange(Array.from(next));
  };

  const clearAll = () => {
    if (selectedIds.size === 0) return;
    onChange([]);
  };

  const visible = assignees.slice(0, maxAvatars);
  const overflow = Math.max(0, assignees.length - visible.length);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-3 text-sm transition-colors hover:bg-secondary/40 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
            'disabled:cursor-not-allowed disabled:opacity-60',
            triggerClassName,
          )}
          aria-label="Edit assignees"
        >
          {assignees.length === 0 ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Users className="h-3.5 w-3.5" />
              <span>Unassigned</span>
            </span>
          ) : assignees.length === 1 ? (
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <Avatar className="h-5 w-5 shrink-0">
                {visible[0].avatar_url && (
                  <AvatarImage src={visible[0].avatar_url} alt="" />
                )}
                <AvatarFallback className="text-[9px]">
                  {initialsOf(visible[0])}
                </AvatarFallback>
              </Avatar>
              <span className="truncate">
                {visible[0].full_name ?? visible[0].email}
              </span>
            </span>
          ) : (
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <span className="flex -space-x-1.5">
                {visible.map((p) => (
                  <Avatar
                    key={p.id}
                    className="h-5 w-5 border-2 border-card"
                    title={p.full_name ?? p.email}
                  >
                    {p.avatar_url && <AvatarImage src={p.avatar_url} alt="" />}
                    <AvatarFallback className="text-[9px]">
                      {initialsOf(p)}
                    </AvatarFallback>
                  </Avatar>
                ))}
                {overflow > 0 && (
                  <div className="z-10 flex h-5 w-5 items-center justify-center rounded-full border-2 border-card bg-secondary text-[9px] font-medium text-muted-foreground">
                    +{overflow}
                  </div>
                )}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                {assignees.length} assignees
              </span>
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn('w-72 p-0', contentClassName)}
      >
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search members…"
            className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {filteredMembers.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              {members.length === 0
                ? 'Share the project with someone first to assign them tasks.'
                : 'No members match.'}
            </div>
          ) : (
            filteredMembers.map((m) => {
              const isSelected = selectedIds.has(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggle(m.id)}
                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm hover:bg-secondary/60"
                >
                  <Avatar className="h-7 w-7">
                    {m.avatar_url && <AvatarImage src={m.avatar_url} alt="" />}
                    <AvatarFallback className="text-[10px]">
                      {initialsOf(m)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-foreground">
                      {m.full_name ?? m.email}
                    </div>
                    {m.full_name && (
                      <div className="truncate text-xs text-muted-foreground">
                        {m.email}
                      </div>
                    )}
                  </div>
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border',
                      isSelected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'opacity-60',
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </span>
                </button>
              );
            })
          )}
        </div>
        {assignees.length > 0 && (
          <>
            <div className="h-px bg-border" />
            <button
              type="button"
              onClick={clearAll}
              className="flex w-full items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:bg-secondary/60"
            >
              <X className="h-3.5 w-3.5" />
              Unassign everyone
            </button>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
